import {
  Box,
  Divider,
  FormControl,
  MenuItem,
  Paper,
  Select,
  styled,
  Switch,
  Typography,
  useMediaQuery
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import useThemeColor from "../../utils/useTheme";
import DateRangePicker from "./DateRangePicker";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAllDiaryExtraData,
  selectAllDiaryOutOf800g,
  selectAllDiaryOutOfCalories,
  selectAllDiaryOutOfProtein,
  selectAllDiaryStats,
  selectAllDiaryStatsCounts
} from "../../features/diary/diarySelectors";
import { getCurrentUserDiaryStats } from "../../features/diary/diarySlice";
import DiaryTracker from "../Extra/DiaryTracker";
import dayjs from "dayjs";
import { ReactComponent as TrophyIcon } from "../../assets/goal-trophy.svg";
import LinearProgress, {
  linearProgressClasses
} from "@mui/material/LinearProgress";
import { useTheme } from "@emotion/react";
import formatNumber from "../../utils/formatNumber";
import Graph from "./Graph";
import { dailyValues } from "../../utils/calculateDVPercent";
// import utc from "dayjs/plugin/utc";
// dayjs.extend(utc);

const BorderLinearProgress = styled(LinearProgress)(
  ({ theme, activeColor, background }) => ({
    height: 10,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: background
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 5,
      backgroundColor: activeColor
    }
  })
);
const includedMicroNutrientFields = [
  "calcium",
  "iron",
  "potassium",
  "sodium",
  "vitamin_a",
  "vitamin_c",
  "vitamin_d"
];

const nutrientFields = (data) => {
  if (data)
    return Object.entries(data).filter(([key, value]) => {
      return includedMicroNutrientFields.includes(key) && !isNaN(Number(value));
    });
  else return [];
};

//   return nutrientFields?.(data)?.map(([key, value]) => {
//     const numericValue = Math.round(parseFloat(value) || 0);
//     // const isSmallValue = numericValue && numericValue < 1;
//     return {
//       name: key
//         .replace(/_/g, " ")
//         .replace(/\b\w/g, (char) => char.toUpperCase()),
//       value: numericValue,
//       unit: "g",
//       percent: Math.round((numericValue / dailyValues[key]) * 100)
//     };
//   });
// };

const StatsList = ({ completedDates, level: { levelName, levelId } }) => {
  const styles = useThemeColor();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [includeToday, setIncludeToday] = useState(true);
  const [value, setValue] = useState("");
  const dispatch = useDispatch();
  const allStats = useSelector(selectAllDiaryStats);
  const allStatsCount = useSelector(selectAllDiaryStatsCounts);
  const extraData = useSelector(selectAllDiaryExtraData);
  const total800g = useSelector(selectAllDiaryOutOf800g);
  const totalProteins = useSelector(selectAllDiaryOutOfProtein);
  const totalCalories = useSelector(selectAllDiaryOutOfCalories);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const selectedFilter = localStorage.getItem("selectedStatFilter");

  useEffect(() => {
    if (selectedFilter) setValue(selectedFilter);
    else setValue("7days");
  }, [selectedFilter, allStats]);
  useEffect(() => {
    if (value)
      if ((value === "custom" && startDate && endDate) || value !== "custom")
        dispatch(
          getCurrentUserDiaryStats({
            timeFrame: value,
            levelId,
            includeToday: value === "today" ? true : includeToday,
            date: dayjs().format("YYYY-MM-DD"),
            ...(value === "custom" && {
              startDate: dayjs(startDate).format("YYYY-MM-DD"),
              endDate: dayjs(endDate).format("YYYY-MM-DD")
            })
          })
        );
  }, [dispatch, includeToday, value, levelId, startDate, endDate]);

  useEffect(() => {
    if (selectedFilter === "custom") {
      const localStorageStartDate = localStorage.getItem("startDate");
      const localStorageEndDate = localStorage.getItem("endDate");

      // Convert string dates from localStorage to dayjs objects
      if (localStorageStartDate && localStorageStartDate !== "Invalid Date") {
        setStartDate(dayjs(localStorageStartDate));
      }

      if (localStorageEndDate && localStorageEndDate !== "Invalid Date") {
        setEndDate(dayjs(localStorageEndDate));
      }
    }
  }, []);

  useEffect(() => {
    if (value === "custom") {
      if (startDate) {
        localStorage.setItem(
          "startDate",
          dayjs(startDate).format("YYYY-MM-DD")
        );
      }

      if (endDate) {
        localStorage.setItem("endDate", dayjs(endDate).format("YYYY-MM-DD"));
      }
    }
  }, [startDate, endDate]);
  const calculateAverage800gms = useMemo(() => {
    const sum = allStats?.reduce(
      (prev, curr) => prev + (curr.totalEntry800g || 0),
      0
    );
    return {
      average:
        Math.round(
          levelName === "level3"
            ? extraData?.averagedSummaries?.metric_serving_amount
            : sum / allStatsCount?.entry800gCount
        ) || 0,
      total: allStats[0]?.outOf800g || total800g
    };
  }, [allStats, total800g]);

  const calculateAverageProtein = useMemo(() => {
    const sum = allStats?.reduce(
      (prev, curr) => prev + (curr.totalProtein || 0),
      0
    );
    return {
      average:
        Math.round(
          levelName === "level3"
            ? extraData?.averagedSummaries?.protein
            : sum / allStatsCount?.proteinCount
        ) || 0,
      total: allStats[0]?.outOfProtein || totalProteins
    };
  }, [allStats, totalProteins]);

  const calculateAverageCalories = useMemo(() => {
    const sum = allStats?.reduce(
      (prev, curr) => prev + (curr.totalProtein || 0),
      0
    );
    return {
      average:
        Math.round(
          levelName === "level3"
            ? extraData?.averagedSummaries?.calories || 0
            : sum / allStats?.length
        ) || 0,
      total: allStats[0]?.outOfCalories || totalCalories
    };
  }, [allStats, totalCalories]);

  const formattedNutrients = nutrientFields(extraData?.averagedSummaries)?.map(
    ([key, value]) => {
      const numericValue = Math.round(parseFloat(value) || 0);
      return {
        name: key
          .replace(/_/g, " ")
          .replace(/\b\w/g, (char) => char.toUpperCase()),
        value: numericValue,
        unit: "g",
        percent: Math.round(((parseFloat(value) || 0) / dailyValues[key]) * 100)
      };
    }
  );

  const getNutrientValue = (name, unit = "") => {
    const nutrient = formattedNutrients?.find((n) => n.name === name);
    if (!nutrient) return `0${unit || ""}(0%)`; // Fallback if nutrient is not found
    const value = nutrient?.value || 0; // Default to 0 if value is undefined or null
    const percent = nutrient?.percent || 0; // Default to 0 if percent is undefined or NaN
    const unitString = unit || nutrient?.unit || ""; // Default to empty string if no unit is provided

    return `${formatNumber(value)}${unitString}${` (${formatNumber(
      percent
    )}%)`}`;
  };

  const NutrientRow = ({ name, unit }) => (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      py={0.5}
    >
      <Typography fontSize="13px">{name}</Typography>
      <Typography fontSize="13px">{getNutrientValue(name, unit)}</Typography>
      {/* <Typography fontSize="13px">{calculatePercentDV(name, unit)}</Typography> */}
    </Box>
  );
  const correctedCalories = Math.round(
    (Number(extraData?.averagedSummaries?.protein) || 0) * 4 +
      (Number(extraData?.averagedSummaries?.carbohydrate) || 0) * 4 +
      (Number(extraData?.averagedSummaries?.fat) || 0) * 9
  );

  const MetricBox = ({ label, value = "", percentage }) => (
    <Box sx={{ minWidth: "90px" }}>
      <Typography
        fontSize="14px"
        color={styles.lightBlack}
        sx={{ textAlign: "left" }}
      >
        {value}
        {label && !label?.startsWith("g") ? ` ${label}` : label}
      </Typography>
      <Typography fontSize="14px" sx={{ textAlign: "left" }}>
        {percentage ? `${percentage}%` : ""}
      </Typography>
    </Box>
  );

  const PillarSection = ({ title, color, data, averages }) => (
    <Box display="flex" flexDirection="column" width="100%">
      <Typography
        variant="body2"
        fontWeight="bold"
        color={styles.color}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          mb: 1
        }}
      >
        <Box
          component="span"
          sx={{
            height: "8px",
            width: "8px",
            backgroundColor: color,
            borderRadius: "50%",
            display: "inline-block"
          }}
        />
        {title}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)", // 2 columns on mobile
            sm: "repeat(3, 1fr)", // 3 columns on tablet
            md: "20% 20% 20% 20% 20%" // 5 equal columns on desktop
          },
          gap: 2,
          mb: 2,
          alignItems: "start"
        }}
      >
        <MetricBox
          label="Cals"
          value={formatNumber(Number(data?.calories)) || 0}
          percentage={
            data?.calories
              ? Math.round(
                  ((Number(data?.calories) || 0) /
                    (extraData?.averagedSummaries?.calories || 1)) *
                    100
                )
              : 0
          }
        />
        <MetricBox
          label="g Protein"
          value={formatNumber(Number(data?.protein)) || 0}
          percentage={
            +data?.protein
              ? Math.round(
                  ((Number(data?.protein) || 0) /
                    extraData?.averagedSummaries?.protein || 1) * 100
                )
              : 0
          }
        />
        <MetricBox
          label="g Carbs"
          value={formatNumber(Number(data?.carbohydrate)) || 0}
          percentage={
            +data?.carbohydrate
              ? Math.round(
                  ((Number(data?.carbohydrate) || 0) /
                    (averages?.carbohydrate || 1)) *
                    100
                )
              : 0
          }
        />
        <MetricBox
          label="g Fiber"
          value={formatNumber(Number(data?.fiber)) || 0}
          percentage={
            +data?.fiber
              ? Math.round(
                  ((Number(data?.fiber) || 0) / (averages?.fiber || 1)) * 100
                )
              : 0
          }
        />
        <MetricBox
          label="g Fat"
          value={formatNumber(Number(data?.fat)) || 0}
          percentage={
            +data?.fat
              ? Math.round(
                  ((Number(data?.fat) || 0) / (averages?.fat || 1)) * 100
                )
              : 0
          }
        />
      </Box>
    </Box>
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={isMobile ? 1.5 : 3}
      mt={isMobile ? -1 : 0}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap" // Allows wrapping on smaller screens
        gap={isMobile ? 2 : 0} // Adds spacing between items for better mobile layout
      >
        {!isMobile ? (
          <Typography fontSize={"24px"} color={styles.color}>
            {/* Diet Statistics */}
          </Typography>
        ) : (
          <></>
        )}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          flexWrap="wrap"
          sx={{ width: { xs: "100%", md: "35%" }, gap: 1 }}
        >
          {!["today", "custom"].includes(value) && (
            <Box
              display="flex"
              alignItems="center"
              sx={{ marginRight: isMobile ? 0 : 2 }}
            >
              <Typography color={styles.color} fontSize={"13px"}>
                Include Today
              </Typography>
              <Switch
                checked={includeToday}
                onChange={(e) => setIncludeToday(e.target.checked)}
              />
            </Box>
          )}
          <FormControl
            sx={{
              minWidth: "140px", // Set minimum width for consistent appearance
              flexGrow: 1, // Adjusts width dynamically for proper alignment
              svg: { fill: styles.color },
              ".MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: styles.lightGreyColor
                },
                "&:hover fieldset": {
                  borderColor: styles.color
                },
                "&.Mui-focused fieldset": {
                  borderColor: styles.color
                }
              }
            }}
          >
            <Select
              labelId="range-label"
              name="range"
              value={value}
              MenuProps={{
                MenuListProps: {
                  sx: {
                    padding: 0,
                    ".Mui-selected": {
                      color: `${styles.background} !important`,
                      background: `${styles.color} !important`
                    }
                  }
                }
              }}
              sx={{
                color: styles.color,
                marginTop: 0,
                paddingTop: 0
              }}
              onChange={(e) => {
                setValue(e.target.value);
                localStorage.setItem("selectedStatFilter", e.target.value);
              }}
            >
              <MenuItem
                sx={{
                  backgroundColor: styles.background,
                  color: styles.color,
                  paddingY: 1,
                  "&:hover": {
                    backgroundColor: styles.color,
                    color: styles.background
                  }
                }}
                value="today"
              >
                Today
              </MenuItem>
              <MenuItem
                sx={{
                  backgroundColor: styles.background,
                  color: styles.color,
                  paddingY: 1,
                  "&:hover": {
                    backgroundColor: styles.color,
                    color: styles.background
                  }
                }}
                value="7days"
              >
                7 Days
              </MenuItem>
              <MenuItem
                sx={{
                  backgroundColor: styles.background,
                  color: styles.color,
                  paddingY: 1,
                  "&:hover": {
                    backgroundColor: styles.color,
                    color: styles.background
                  }
                }}
                value="14days"
              >
                14 Days
              </MenuItem>
              <MenuItem
                sx={{
                  backgroundColor: styles.background,
                  color: styles.color,
                  paddingY: 1,
                  "&:hover": {
                    backgroundColor: styles.color,
                    color: styles.background
                  }
                }}
                value="custom"
              >
                Custom time
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      {value === "custom" && (
        <Box display="flex" justifyContent="flex-end">
          <DateRangePicker
            allCompletedData={completedDates}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
        </Box>
      )}
      <Box
        display="flex"
        justifyContent="center"
        sx={{
          borderRadius: "16px",
          backgroundColor: styles.diaryTracker
        }}
      >
        <DiaryTracker
          title={"#800gChallenge"}
          nonEditable
          levelName={levelName}
          strokeColor={"#F6541C"}
          value={calculateAverage800gms?.average}
          maxValue={calculateAverage800gms?.total}
          heightX={
            levelName === "level2"
              ? isMobile
                ? 130
                : 210
              : isMobile
              ? 170
              : 250
          }
          widthX={
            levelName === "level2"
              ? isMobile
                ? 190
                : 300
              : isMobile
              ? 230
              : 350
          }
        />
        {levelName !== "level1" && (
          <DiaryTracker
            title={"Protein"}
            nonEditable
            levelName={levelName}
            strokeColor={"#368AD0"}
            value={calculateAverageProtein?.average}
            maxValue={calculateAverageProtein?.total}
            heightX={levelName === "level2" ? (isMobile ? 130 : 210) : 250}
            widthX={levelName === "level2" ? (isMobile ? 190 : 300) : 350}
          />
        )}
        {levelName === "level3" && (
          <DiaryTracker
            isCalories
            nonEditable
            levelName={levelName}
            value={calculateAverageCalories?.average}
            maxValue={calculateAverageCalories?.total}
            strokeColor={"#FF9431"}
            title={"Calories"}
            heightX={levelName === "level2" ? 210 : 250}
            widthX={levelName === "level2" ? 300 : 350}
          />
        )}
      </Box>

      {levelName === "level3" ? (
        <>
          <Box>
            <Typography fontSize="15px" color={styles.inputLabelColor}>
              Protein Pillar
            </Typography>
            <Box
              mt={1}
              width="100%"
              borderRadius="8px"
              padding={"18px"}
              sx={{ background: styles.lightGreyColor }}
            >
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                flexDirection="column"
                alignItems={"start"}
                gap="10px"
              >
                <Box
                  sx={{ width: { xs: "100%", md: "40%" } }}
                  display={"flex"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Box>
                    <Typography fontWeight="bold" fontSize="14px">
                      {Math.round(
                        ((Number(
                          +extraData?.totalSummaries?.proteinSummary?.protein ||
                            0
                        ) *
                          4) /
                          Number(
                            extraData?.totalSummaries?.proteinSummary?.calories
                          )) *
                          100 || 0
                      )}
                      % Protein
                    </Typography>
                  </Box>
                  <Box>
                    <Typography fontWeight="bold" fontSize="14px">
                      {Math.round(
                        Number(
                          +extraData?.totalSummaries?.proteinSummary
                            ?.calories || 0
                        ) -
                          (Number(
                            extraData?.totalSummaries?.proteinSummary?.protein
                          ) * 4 || 0)
                      ) > 0
                        ? Math.round(
                            Number(
                              +extraData?.totalSummaries?.proteinSummary
                                ?.calories || 0
                            ) -
                              (Number(
                                extraData?.totalSummaries?.proteinSummary
                                  ?.protein
                              ) * 4 || 0)
                          )
                        : 0}{" "}
                      Carb + Fat Cals
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box>
            <Typography fontSize="15px" color={styles.inputLabelColor}>
              Three Pillars Method
            </Typography>
            <Box
              mt={1}
              width="100%"
              borderRadius="8px"
              padding="18px"
              sx={{ background: styles.lightGreyColor }}
            >
              <PillarSection
                title="800g"
                color="#F6541C"
                data={extraData?.totalSummaries?.entry800gSummary}
                averages={extraData?.averagedSummaries}
              />
              <Divider
                sx={{ background: "#8E8E8E", width: "100%", m: "10px 0" }}
              />
              <PillarSection
                title="Protein"
                color="#368AD0"
                data={extraData?.totalSummaries?.proteinSummary}
                averages={extraData?.averagedSummaries}
              />
              <Divider
                sx={{ background: "#8E8E8E", width: "100%", m: "10px 0" }}
              />
              <PillarSection
                title="Other"
                color="#FF9431"
                data={extraData?.totalSummaries?.otherFoodSummary}
                averages={extraData?.averagedSummaries}
              />
            </Box>
          </Box>
          <Box>
            <Typography fontSize="15px" color={styles.inputLabelColor}>
              Macronutrients
            </Typography>
            <Box
              mt={1}
              width="100%"
              borderRadius="8px"
              padding="18px"
              sx={{ background: styles.lightGreyColor }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                flexDirection="column"
                alignItems="start"
                gap="10px"
              >
                <Box
                  mt={1}
                  width="100%"
                  borderRadius="8px"
                  sx={{ background: styles.lightGreyColor }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "repeat(2, 1fr)", // 2 columns on mobile
                        sm: "repeat(3, 1fr)", // 3 columns on tablet
                        md: "20% 20% 20% 20% 20%" // 5 equal columns on desktop
                      },
                      gap: 2,
                      mb: 2,
                      alignItems: "start"
                    }}
                  >
                    <MetricBox
                      label="Cals"
                      value={calculateAverageCalories?.average}
                    />
                    <MetricBox
                      label="Corrected Cals"
                      value={correctedCalories}
                    />
                    <MetricBox
                      label="g Protein"
                      value={
                        formatNumber(
                          Number(extraData?.averagedSummaries?.protein)
                        ) || 0
                      }
                      percentage={Math.round(
                        (((Number(extraData?.averagedSummaries?.protein) || 0) *
                          4) /
                          correctedCalories || 0) * 100
                      )}
                    />
                    <MetricBox
                      label="g Carbs"
                      value={
                        formatNumber(
                          Number(extraData?.averagedSummaries?.carbohydrate)
                        ) || 0
                      }
                      percentage={Math.round(
                        (((Number(extraData?.averagedSummaries?.carbohydrate) ||
                          0) *
                          4) /
                          correctedCalories || 0) * 100
                      )}
                    />
                    <MetricBox
                      label="g Fat"
                      value={
                        formatNumber(
                          Number(extraData?.averagedSummaries?.fat)
                        ) || 0
                      }
                      percentage={Math.round(
                        (((Number(extraData?.averagedSummaries?.fat) || 0) *
                          9) /
                          correctedCalories || 0) * 100
                      )}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "repeat(2, 1fr)", // 2 columns on mobile
                        sm: "repeat(3, 1fr)", // 3 columns on tablet
                        md: "20% 20% 20% 20% 20%" // 5 equal columns on desktop
                      },
                      gap: 2,
                      mb: 2,
                      alignItems: "start"
                    }}
                  >
                    {isMobile ? (
                      <></>
                    ) : (
                      <>
                        <MetricBox />
                        <MetricBox />
                        <MetricBox />
                      </>
                    )}
                    <MetricBox
                      label="g Fiber"
                      value={
                        formatNumber(
                          Number(extraData?.averagedSummaries?.fiber)?.toFixed(
                            2
                          )
                        ) || 0
                      }
                    />
                    <MetricBox
                      label="g Saturated Fat"
                      value={
                        formatNumber(
                          Number(
                            extraData?.averagedSummaries?.saturated_fat
                          )?.toFixed(2)
                        ) || 0
                      }
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          {extraData?.averagedSummaries &&
          Object.keys(extraData?.averagedSummaries)?.length ? (
            <Box>
              <Typography fontSize="15px" color={styles.inputLabelColor}>
                Select Micronutrients
              </Typography>
              <Box
                width="100%"
                padding={"18px"}
                sx={{
                  background: styles.lightGreyColor,
                  borderRadius: 2,
                  mt: 1
                }}
                borderRadius={1}
              >
                {/* Vitamins and Minerals Group */}
                <NutrientRow name="Calcium" unit="mg" />
                <NutrientRow name="Iron" unit="mg" />
                <NutrientRow name="Potassium" unit="mg" />
                <NutrientRow name="Sodium" unit="mg" />
                <NutrientRow name="Vitamin A" unit="mcg" />
                <NutrientRow name="Vitamin C" unit="mg" />
                <NutrientRow name="Vitamin D" unit="mcg" />
              </Box>
              {/* <Box
                mt={1}
                width="100%"
                display={"flex"}
                justifyContent={"space-between"}
                flexDirection="column"
                alignItems={"center"}
                gap={0.6}
                borderRadius="8px"
                padding={"18px"}
                sx={{ background: styles.lightGreyColor }}
              >
                {formattedNutrients(extraData?.averagedSummaries)?.map(
                  (item) => (
                    <Box
                      width="100%"
                      display={"flex"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                    >
                      <Typography fontSize="13px">{item.name}</Typography>
                      <Typography fontSize="13px">
                        {item.value + item.unit}
                      </Typography>
                    </Box>
                  )
                )}
              </Box> */}
            </Box>
          ) : (
            <></>
          )}
        </>
      ) : (
        <></>
      )}

      <Box display="flex" flexDirection="column" gap={2} mb={2}>
        <Graph
          levelName={levelName}
          total800g={total800g}
          totalProteins={totalProteins}
          totalCalories={totalCalories}
          data={allStats}
        />
      </Box>
      {allStats
        ?.filter((item) => item?.notes)
        ?.map((item) => (
          <Paper
            elevation={3}
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: styles.modBackgroundBlocks,
              boxShadow: "0 4px 20px 0 rgba(0,0,0,0.1)"
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start"
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: styles.yellow
                  }}
                >
                  {dayjs.utc(item?.currentDayDate).format("DD MMM")}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: styles.inputLabelColor
                  }}
                >
                  {item?.notes}
                </Typography>
              </Box>
            </Box>
          </Paper>
        ))}
    </Box>
  );
};

export default StatsList;
